import { useEffect } from 'react'
import { useIntercom as useIntercomOriginal } from 'react-use-intercom'
import { openChatwootCustomerChat } from '../ChatwootCustomer/openChatwootCustomerChat'
import { useChatwootEnabledForUserState } from '../../lib/useChatwootEnabled'
import { forceShutdownIntercom } from './forceShutdownIntercom'

const noop = () => {}

/**
 * Safe wrapper around useIntercom that handles cases where IntercomProvider is not available.
 * Returns noop functions when the provider is not in the component tree.
 */
export function useSafeIntercom() {
  const { enabled: enableChatwoot, loading: chatwootLoading } =
    useChatwootEnabledForUserState()

  // Kill-switch for the self-booted visitor messenger (CL-1790): the Intercom
  // widget script starts a visitor-mode launcher on its own when it finds the
  // pre-set `window.intercomSettings.app_id` — no boot() involved, so the gated
  // booters (and react-use-intercom's guarded shutdown()) can't remove it. Once
  // the per-user gate RESOLVES enabled, force-shut it down at the source. Runs on
  // every surface that uses this wrapper (admin/operations/employer shells).
  useEffect(() => {
    if (enableChatwoot && !chatwootLoading) {
      forceShutdownIntercom()
    }
  }, [enableChatwoot, chatwootLoading])

  try {
    const intercom = useIntercomOriginal()

    if (enableChatwoot) {
      return {
        ...intercom,
        boot: noop,
        show: openChatwootCustomerChat,
        showMessages: openChatwootCustomerChat,
        showNewMessages: openChatwootCustomerChat,
      }
    }

    return intercom
  } catch {
    // IntercomProvider not available - return noop functions
    return {
      boot: noop,
      shutdown: noop,
      hardShutdown: noop,
      update: noop,
      hide: noop,
      show: enableChatwoot ? openChatwootCustomerChat : noop,
      showMessages: enableChatwoot ? openChatwootCustomerChat : noop,
      showNewMessages: enableChatwoot ? openChatwootCustomerChat : noop,
      onHide: noop,
      onShow: noop,
      onUnreadCountChange: noop,
      trackEvent: () => {},
      getVisitorId: () => '',
      startTour: noop,
      startChecklist: noop,
      showArticle: noop,
      showNews: noop,
      startSurvey: noop,
      showSpace: noop,
      showTicket: noop,
      showConversation: noop,
    }
  }
}
